import { useRef, useState } from 'react';
import { isProductionEnv, isProductionOrIntegrationEnv } from '@alfa-bank/newclick-core/utils';
import { AudioCodesUA } from '../../../assets/acapi.1.18.0';
import { causes, checkAvailableDevicesErrors, phoneListeners, voipScreens, } from '../../../constants/types';
import { callerPhoneNumber, sbcDisconnectCounterMax } from '../../../constants/voip-settings';
import { useWebview } from '../../../hooks/use-webview';
import { useWidgetContext } from '../../../hooks/use-widget-context';
import { useTimer } from './use-timer';
const url = `wss://${window.location.hostname}/api/v1/voip-call-interaction`;
const getAddressVoip = (nodeEnv, isPreAuth) => {
    if (isPreAuth && nodeEnv && isProductionEnv(nodeEnv)) {
        return 'wss://amcalls.alfabank.ru:8444';
    }
    if (isPreAuth) {
        return 'wss://testamcalls.alfabank.ru:8444';
    }
    if (nodeEnv && isProductionOrIntegrationEnv(nodeEnv)) {
        return url;
    }
    return 'wss://testamcalls.alfabank.ru:8444';
};
const getDomain = (nodeEnv, isPreAuth) => {
    if (isPreAuth && nodeEnv && isProductionEnv(nodeEnv)) {
        return 'amcalls.alfabank.ru';
    }
    if (isPreAuth) {
        return 'testamcalls.alfabank.ru';
    }
    if (nodeEnv && isProductionEnv(nodeEnv)) {
        return 'amcalls.alfabank.ru';
    }
    return 'testamcalls.alfabank.ru';
};
export const useVoip = () => {
    const { nodeEnv, phoneNumber, trackInAnalytics, setCanOpenedPopup, isPreAuth } = useWidgetContext();
    const { isWebview } = useWebview();
    const serverConfig = {
        domain: getDomain(nodeEnv, !!isPreAuth),
        addresses: [getAddressVoip(nodeEnv, !!isPreAuth)],
        iceServers: [],
    };
    const remoteAudioVideo = useRef(null);
    const refCall = useRef(new AudioCodesUA());
    const phone = refCall.current;
    const activeCallRef = useRef(null);
    const sbcDisconnectCounter = useRef(0);
    const [haveDevices, setHaveDevices] = useState(null);
    const [accessMicroStatus, setAccessMicroStatus] = useState(null);
    const [loginStatus, setLoginStatus] = useState(null);
    const [activePhoneListener, setActivePhoneListener] = useState(null);
    const [cause, setCause] = useState(null);
    const [voipScreen, setVoipScreen] = useState(voipScreens.WITHOUT_CALL);
    const [muteAudio, setMuteAudio] = useState(false);
    const { clearTimer, isActiveTimer, setIsActiveTimer, timerText } = useTimer();
    const clearMicroStates = () => {
        setLoginStatus(null);
        setAccessMicroStatus(null);
        setHaveDevices(null);
    };
    const initSipStack = (account, serverConfigArg) => {
        phone.setServerConfig(serverConfigArg.addresses, serverConfigArg.domain, serverConfigArg.iceServers);
        phone.setAccount(account.user, account.displayName, account.password);
        // Set phone API listeners
        phone.setListeners({
            async loginStateChanged(_isLogin, causeArg) {
                setActivePhoneListener(phoneListeners.LOGIN_STATE_CHANGED);
                setLoginStatus(causeArg);
                switch (causeArg) {
                    case 'connected':
                        if (!activeCallRef.current) {
                            activeCallRef.current = phone.call(phone.AUDIO, phoneNumber, [
                                'ID_SYSTEM: AO',
                            ]);
                            trackInAnalytics('VOIP_CONNECT_CALL', {
                                status: 'Success',
                            });
                        }
                        break;
                    case 'disconnected':
                        if (phone.isInitialized() && activeCallRef.current) {
                            return;
                        }
                        clearTimer();
                        if (phone.isInitialized() && !activeCallRef.current) {
                            sbcDisconnectCounter.current += 1;
                            if (sbcDisconnectCounter.current >= sbcDisconnectCounterMax) {
                                sbcDisconnectCounter.current = 0;
                                setVoipScreen(voipScreens.FAILED_CALL);
                                trackInAnalytics('VOIP_ERROR_CONNECT_CALL', {
                                    errorMessage: 'Error > Соединение не было установлено',
                                });
                                phone.deinit();
                            }
                            return;
                        }
                        if (phone.isInitialized()) {
                            const notFailedStatuses = [
                                voipScreens.TERMINATED_CALL,
                                voipScreens.CANCELLED_CALL,
                                voipScreens.WITHOUT_CALL,
                            ];
                            setVoipScreen((currentValue) => {
                                if (notFailedStatuses.includes(currentValue)) {
                                    return currentValue;
                                }
                                return voipScreens.FAILED_CALL;
                            });
                        }
                        break;
                }
            },
            outgoingCallProgress() {
                setActivePhoneListener(phoneListeners.OUTGOING_CALL_PROGRESS);
            },
            callTerminated(_call, _message, causeArg) {
                setCause(causeArg);
                setActivePhoneListener(phoneListeners.CALL_TERMINATED);
                switch (causeArg) {
                    case causes.USER_DENIED_MEDIA_ACCESS:
                        setAccessMicroStatus('DENIED');
                        break;
                    case causes.CANCELED:
                        if (phone.isInitialized())
                            setVoipScreen(voipScreens.FAILED_CALL);
                        break;
                    case causes.BYE:
                        setVoipScreen(voipScreens.TERMINATED_CALL);
                        break;
                    default:
                        setVoipScreen(voipScreens.TERMINATED_CALL);
                        trackInAnalytics('VOIP_ERROR_CONNECT_CALL', {
                            errorMessage: `Error > Call termination, reason: ${causeArg}`,
                        });
                }
                activeCallRef.current = null;
                setMuteAudio(false);
                // Disconnect from SBC server.
                phone.deinit();
            },
            callConfirmed(_call, _message, causeArg) {
                setCause(causeArg);
                setActivePhoneListener(phoneListeners.CALL_CONFIRMED);
                setIsActiveTimer(true);
            },
            callShowStreams(_call, _localStream, remoteStream) {
                setActivePhoneListener(phoneListeners.CALL_SHOW_STREAM);
                if (remoteAudioVideo.current) {
                    remoteAudioVideo.current.srcObject = remoteStream;
                }
            },
            incomingCall(call) {
                setActivePhoneListener(phoneListeners.INCOMING_CALL);
                call.reject();
            },
            callHoldStateChanged() {
                setActivePhoneListener(phoneListeners.CALL_HOLD_STATE_CHANGED);
            },
        });
        setVoipScreen(voipScreens.WITH_CALL);
        if (isWebview) {
            setCanOpenedPopup(true);
        }
        phone.init(false);
    };
    const startCall = async (userPhone = callerPhoneNumber) => {
        let getUserMedia;
        try {
            await phone.checkAvailableDevices();
            getUserMedia = await navigator.mediaDevices.getUserMedia({ audio: true });
            initSipStack({ user: userPhone, displayName: userPhone, password: '' }, serverConfig);
            const tracks = getUserMedia.getTracks();
            tracks.map((track) => track.stop());
        }
        catch (error) {
            if (Object.values(checkAvailableDevicesErrors).includes(error)) {
                setHaveDevices('NOT_MICRO');
                return;
            }
            if (!getUserMedia) {
                setAccessMicroStatus('DENIED');
                return;
            }
            throw new Error();
        }
    };
    const guiMuteAudio = () => {
        trackInAnalytics('MICROPHONE');
        if (!activeCallRef.current) {
            return;
        }
        const muted = activeCallRef.current.isAudioMuted();
        activeCallRef.current.muteAudio(!muted);
        setMuteAudio(!muted);
    };
    const cancelCall = () => {
        trackInAnalytics('VOIP_CANCEL_CALL');
        setVoipScreen(voipScreens.CANCELLED_CALL);
        phone.deinit();
    };
    return {
        accessMicroStatus,
        setAccessMicroStatus,
        activeCall: activeCallRef.current,
        activePhoneListener,
        cancelCall,
        cause,
        clearMicroStates,
        guiMuteAudio,
        haveDevices,
        initSipStack,
        loginStatus,
        muteAudio,
        phone,
        remoteAudioVideo,
        setLoginStatus,
        setMuteAudio,
        setVoipScreen,
        startCall,
        timerText,
        voipScreen,
        isActiveTimer,
    };
};
